import { useSEO } from "@/lib/seo";
import PageHeader from "@/components/sections/PageHeader";
import AppointmentForm from "@/components/sections/AppointmentForm";
import { SITE } from "@/lib/site";
import { Phone, Mail, MapPin, MessageCircle, Clock } from "lucide-react";

export default function ContactPage() {
  useSEO({
    title: "Book Appointment · Contact Dr. Anusha Mandava",
    description:
      "Book a consultation with Dr. Anusha Mandava, Radiation Oncologist in Hyderabad. Call, WhatsApp or submit an appointment request.",
    keywords:
      "Book Radiation Oncologist Appointment Hyderabad, Cancer Specialist Appointment Hyderabad",
    path: "/contact",
  });

  const waLink = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(SITE.whatsappMessage)}`;

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact & Appointment"
        title="Book a consultation with Dr. Anusha."
        description="Share your details and our team will reach out to confirm your appointment. For urgent enquiries, please call directly."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-700">
              Reach us directly
            </div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl text-slate-900 tracking-tight leading-tight">
              Multiple ways to connect.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Choose the option most convenient for you. Our team will
              coordinate the appointment based on Dr. Anusha&apos;s schedule and
              your urgency.
            </p>

            <div className="mt-8 space-y-3">
              <ContactItem
                icon={Phone}
                label="Call the clinic"
                value={SITE.phone}
                href={`tel:${SITE.phoneRaw}`}
                testId="contact-phone"
              />
              <ContactItem
                icon={MessageCircle}
                label="WhatsApp"
                value="Chat with our team"
                href={waLink}
                external
                testId="contact-whatsapp"
                accent="emerald"
              />
              <ContactItem
                icon={Mail}
                label="Email"
                value={SITE.email}
                href={`mailto:${SITE.email}`}
                testId="contact-email"
              />

              <ContactItem
                icon={Clock}
                label="Consulting hours"
                value={SITE.hours}
                testId="contact-hours"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <AppointmentForm />
          </div>
        </div>
      </section>


    </div>
  );
}

const ContactItem = ({ icon: Icon, label, value, href, external, testId, accent }) => {
  const iconBg = accent === "emerald" ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-blue-700";
  const content = (
    <div className="flex items-start gap-4 p-5 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-slate-50/50 transition-colors">
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
          {label}
        </div>
        <div className="mt-1 font-medium text-slate-900">{value}</div>
      </div>
    </div>
  );
  if (!href) return <div data-testid={testId}>{content}</div>;
  return (
    <a
      href={href}
      data-testid={testId}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className="block"
    >
      {content}
    </a>
  );
};
